/**
 * Dealer Directory public visibility (Dealer Directory M2/M4).
 */
import {
  DEALER_DIRECTORY_EXCLUSION_REASONS,
  DEALER_DIRECTORY_INCLUDABLE_STATES,
  DEALER_DIRECTORY_PUBLIC_STATE,
  PUBLIC_DEALER_DIRECTORY_PATH_PREFIX,
} from "../../constants/dealerDirectoryConstants";
import { DEALER_PROFILE_PAGE_STATE, DEALER_PUBLIC_STATE_MESSAGES_ES } from "../../constants/dealerProfileConstants";
import {
  canShowDealerProfileLink,
  getDealerProfilePublicState,
} from "../dealerProfile/dealerPublicState";
import { isValidDealerSlug } from "../dealerProfile/dealerSlug";

const PROFILE_TO_DIRECTORY_STATE = {
  [DEALER_PROFILE_PAGE_STATE.PUBLIC]: DEALER_DIRECTORY_PUBLIC_STATE.PUBLIC,
  [DEALER_PROFILE_PAGE_STATE.PUBLIC_NO_INVENTORY]: DEALER_DIRECTORY_PUBLIC_STATE.PUBLIC_NO_INVENTORY,
  [DEALER_PROFILE_PAGE_STATE.PUBLIC_CONTACT_LIMITED]: DEALER_DIRECTORY_PUBLIC_STATE.PUBLIC_CONTACT_LIMITED,
  [DEALER_PROFILE_PAGE_STATE.HIDDEN]: DEALER_DIRECTORY_PUBLIC_STATE.HIDDEN,
  [DEALER_PROFILE_PAGE_STATE.INACTIVE]: DEALER_DIRECTORY_PUBLIC_STATE.INACTIVE,
  [DEALER_PROFILE_PAGE_STATE.SUSPENDED]: DEALER_DIRECTORY_PUBLIC_STATE.SUSPENDED,
  [DEALER_PROFILE_PAGE_STATE.REJECTED]: DEALER_DIRECTORY_PUBLIC_STATE.REJECTED,
  [DEALER_PROFILE_PAGE_STATE.DELETED]: DEALER_DIRECTORY_PUBLIC_STATE.DELETED,
  [DEALER_PROFILE_PAGE_STATE.UNKNOWN]: DEALER_DIRECTORY_PUBLIC_STATE.UNKNOWN,
  [DEALER_PROFILE_PAGE_STATE.INVALID]: DEALER_DIRECTORY_PUBLIC_STATE.INVALID,
  [DEALER_PROFILE_PAGE_STATE.NOT_FOUND]: DEALER_DIRECTORY_PUBLIC_STATE.UNKNOWN,
  [DEALER_PROFILE_PAGE_STATE.ERROR]: DEALER_DIRECTORY_PUBLIC_STATE.ERROR,
};

const INCLUDABLE_STATE_SET = new Set(DEALER_DIRECTORY_INCLUDABLE_STATES);

const STATE_TO_EXCLUSION_REASON = {
  [DEALER_DIRECTORY_PUBLIC_STATE.HIDDEN]: DEALER_DIRECTORY_EXCLUSION_REASONS.STATE_HIDDEN,
  [DEALER_DIRECTORY_PUBLIC_STATE.INACTIVE]: DEALER_DIRECTORY_EXCLUSION_REASONS.STATE_INACTIVE,
  [DEALER_DIRECTORY_PUBLIC_STATE.SUSPENDED]: DEALER_DIRECTORY_EXCLUSION_REASONS.STATE_SUSPENDED,
  [DEALER_DIRECTORY_PUBLIC_STATE.REJECTED]: DEALER_DIRECTORY_EXCLUSION_REASONS.STATE_REJECTED,
  [DEALER_DIRECTORY_PUBLIC_STATE.DELETED]: DEALER_DIRECTORY_EXCLUSION_REASONS.STATE_DELETED,
  [DEALER_DIRECTORY_PUBLIC_STATE.UNKNOWN]: DEALER_DIRECTORY_EXCLUSION_REASONS.STATE_UNKNOWN,
  [DEALER_DIRECTORY_PUBLIC_STATE.INVALID]: DEALER_DIRECTORY_EXCLUSION_REASONS.STATE_INVALID,
  [DEALER_DIRECTORY_PUBLIC_STATE.ERROR]: DEALER_DIRECTORY_EXCLUSION_REASONS.STATE_ERROR,
};

function resolveDirectoryBuyerMessage(state, profileState) {
  if (INCLUDABLE_STATE_SET.has(state)) {
    if (state === DEALER_DIRECTORY_PUBLIC_STATE.PUBLIC_NO_INVENTORY) {
      return DEALER_PUBLIC_STATE_MESSAGES_ES.public_no_inventory;
    }
    if (state === DEALER_DIRECTORY_PUBLIC_STATE.PUBLIC_CONTACT_LIMITED) {
      return DEALER_PUBLIC_STATE_MESSAGES_ES.public_contact_limited;
    }
    return null;
  }
  return (
    DEALER_PUBLIC_STATE_MESSAGES_ES[state] ||
    profileState?.buyerMessage ||
    DEALER_PUBLIC_STATE_MESSAGES_ES.unknown
  );
}

function hasSafeDisplayName(dealerOrItem) {
  const name = dealerOrItem?.displayName || dealerOrItem?.name;
  return Boolean(String(name || "").trim());
}

function isSafeProfileHref(href) {
  if (!href || typeof href !== "string") return false;
  const path = href.split("?")[0];
  const profileMatch = path.match(/\/concesionarios\/([^/?#]+)$/);
  if (profileMatch) {
    return isValidDealerSlug(decodeURIComponent(profileMatch[1]));
  }
  if (path.startsWith(`${PUBLIC_DEALER_DIRECTORY_PATH_PREFIX}/`)) {
    return isValidDealerSlug(path.replace(`${PUBLIC_DEALER_DIRECTORY_PATH_PREFIX}/`, ""));
  }
  return false;
}

/**
 * @param {import('../dealerProfile/dealerProfileTypes').DealerProfile|import('./dealerDirectoryTypes').DealerDirectoryItem|null|undefined} dealerOrItem
 * @param {{ listings?: object[], activeInventoryCount?: number|null }} [options]
 */
export function getDealerDirectoryPublicState(dealerOrItem, options = {}) {
  const profileState = getDealerProfilePublicState(dealerOrItem, options);
  const state = PROFILE_TO_DIRECTORY_STATE[profileState.state] || DEALER_DIRECTORY_PUBLIC_STATE.UNKNOWN;

  const slugValid = isValidDealerSlug(dealerOrItem?.slug);
  const profileLinkEligible = canShowDealerProfileLink(dealerOrItem, options) && slugValid;
  const includableState = INCLUDABLE_STATE_SET.has(state);
  const hasName = hasSafeDisplayName(dealerOrItem);

  const canShowProfileLink = profileLinkEligible && includableState;
  const canShowInDirectory = includableState && canShowProfileLink && hasName;
  const canShowInventoryCount = includableState;
  const canShowContactAvailability = includableState;
  const canIndexDirectoryEntry =
    state === DEALER_DIRECTORY_PUBLIC_STATE.PUBLIC ||
    state === DEALER_DIRECTORY_PUBLIC_STATE.PUBLIC_NO_INVENTORY;

  return {
    state,
    /** @deprecated use `state` — kept for M2 item compatibility */
    publicState: state,
    canShowInDirectory,
    canShowProfileLink,
    canShowInventoryCount,
    canShowContactAvailability,
    canIndexDirectoryEntry,
    reason: state,
    buyerMessage: resolveDirectoryBuyerMessage(state, profileState),
    internalReason: profileState.internalReason || null,
    profilePublicState: profileState,
  };
}

/**
 * @param {import('./dealerDirectoryTypes').DealerDirectoryItem|null|undefined} item
 */
export function getDealerDirectoryExclusionReason(item) {
  if (!item) return DEALER_DIRECTORY_EXCLUSION_REASONS.MISSING_ITEM;
  if (!hasSafeDisplayName(item)) return DEALER_DIRECTORY_EXCLUSION_REASONS.MISSING_DISPLAY_NAME;
  if (!isValidDealerSlug(item.slug)) return DEALER_DIRECTORY_EXCLUSION_REASONS.INVALID_SLUG;

  const stateReason = STATE_TO_EXCLUSION_REASON[item.publicState];
  if (stateReason) return stateReason;

  if (!item.canShowInDirectory) return DEALER_DIRECTORY_EXCLUSION_REASONS.NOT_DIRECTORY_ELIGIBLE;
  if (!item.canShowProfileLink) return DEALER_DIRECTORY_EXCLUSION_REASONS.UNSAFE_PROFILE_LINK;
  if (!item.profileHref || !isSafeProfileHref(item.profileHref)) {
    return DEALER_DIRECTORY_EXCLUSION_REASONS.MISSING_PROFILE_HREF;
  }

  return null;
}

function directoryItemScore(item) {
  let score = 0;
  if (item.canShowProfileLink && isSafeProfileHref(item.profileHref)) score += 1000;
  if (item.isVerified) score += 100;
  if (item.hasActiveInventory) score += 50;
  score += Math.min(item.activeInventoryCount ?? 0, 40);
  if (item.lastInventoryUpdatedAt) {
    const ts = new Date(item.lastInventoryUpdatedAt).getTime();
    if (!Number.isNaN(ts)) score += ts / 1_000_000_000_000;
  }
  return score;
}

/**
 * @param {import('./dealerDirectoryTypes').DealerDirectoryItem} item
 */
function getDealerDirectoryDedupeKey(item) {
  if (item.id) return `id:${item.id}`;
  if (item.slug) return `slug:${item.slug}`;
  const name = item.sortName || String(item.displayName || "").trim().toLowerCase();
  const city = String(item.city || "").trim().toLowerCase();
  if (name && city) return `namecity:${name}|${city}`;
  if (name) return `name:${name}`;
  return null;
}

/**
 * @param {import('./dealerDirectoryTypes').DealerDirectoryItem[]} items
 * @param {{ warnings?: string[] }} [options]
 */
export function dedupeDirectoryDealers(items = [], options = {}) {
  const warnings = [...(options.warnings || [])];
  const excludedByReason = {};
  /** @type {Map<string, import('./dealerDirectoryTypes').DealerDirectoryItem>} */
  const byKey = new Map();
  let duplicateMerged = 0;

  items.forEach((item) => {
    const key = getDealerDirectoryDedupeKey(item);
    if (!key) {
      warnings.push("dedupe_skipped_missing_key");
      return;
    }

    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, item);
      return;
    }

    duplicateMerged += 1;
    const keep = directoryItemScore(item) >= directoryItemScore(existing) ? item : existing;
    byKey.set(key, keep);
    if (process.env.NODE_ENV === "development") {
      warnings.push(`duplicate_dealer_merged:${key}`);
    }
  });

  if (duplicateMerged > 0) {
    excludedByReason[DEALER_DIRECTORY_EXCLUSION_REASONS.DUPLICATE_MERGED] = duplicateMerged;
  }

  return {
    items: [...byKey.values()],
    warnings,
    excludedByReason,
    duplicateMerged,
  };
}

/**
 * @param {import('./dealerDirectoryTypes').DealerDirectoryItem} item
 */
function sanitizeVisibleDirectoryItem(item) {
  const safeHref = isSafeProfileHref(item.profileHref) ? item.profileHref : null;
  return {
    ...item,
    profileHref: item.canShowProfileLink && safeHref ? safeHref : null,
    canShowInDirectory: true,
    canShowProfileLink: Boolean(item.canShowProfileLink && safeHref),
  };
}

function logDevDirectoryExclusion(item, reason) {
  if (process.env.NODE_ENV !== "development") return;
  console.debug("[dealer-directory-visibility]", {
    reason,
    dealerId: item.id || null,
    dealerSlug: item.slug || null,
  });
}

/**
 * @param {import('./dealerDirectoryTypes').DealerDirectoryItem[]} items
 * @param {{ warnings?: string[], logExclusions?: boolean }} [options]
 */
export function applyPublicDirectoryDirectoryFilter(items = [], options = {}) {
  const warnings = [...(options.warnings || [])];
  const excludedByReason = {};
  const visibleItems = [];

  items.forEach((item) => {
    const reason = getDealerDirectoryExclusionReason(item);
    if (reason) {
      excludedByReason[reason] = (excludedByReason[reason] || 0) + 1;
      if (options.logExclusions) logDevDirectoryExclusion(item, reason);
      return;
    }
    visibleItems.push(sanitizeVisibleDirectoryItem(item));
  });

  return {
    visibleItems,
    excludedCount: items.length - visibleItems.length,
    excludedByReason,
    warnings,
  };
}

/**
 * @param {import('../dealerProfile/dealerProfileTypes').DealerProfile|import('./dealerDirectoryTypes').DealerDirectoryItem|null|undefined} dealerOrItem
 * @param {{ listings?: object[], activeInventoryCount?: number|null }} [options]
 */
export function canShowDealerInDirectory(dealerOrItem, options = {}) {
  if (!dealerOrItem) return false;
  if (Object.prototype.hasOwnProperty.call(dealerOrItem, "canShowInDirectory")) {
    return (
      dealerOrItem.canShowInDirectory === true &&
      dealerOrItem.canShowProfileLink === true &&
      isSafeProfileHref(dealerOrItem.profileHref)
    );
  }
  return getDealerDirectoryPublicState(dealerOrItem, options).canShowInDirectory;
}

/**
 * @param {import('./dealerDirectoryTypes').DealerDirectoryItem[]} items
 */
export function filterPublicDirectoryDealers(items = []) {
  return applyPublicDirectoryDirectoryFilter(items).visibleItems;
}

/**
 * @param {string|null|undefined} slug
 */
export function isDealerDirectorySlugValid(slug) {
  return isValidDealerSlug(slug);
}

/**
 * @param {string|null|undefined} href
 */
export function isDealerDirectoryProfileHrefSafe(href) {
  return isSafeProfileHref(href);
}
